/** Compares a partner's plans with Pawer's standard plans (spec 033). */

/** How a partner coverage differs from the standard plan it is compared with. */
export const DIFF = {
  SAME: 'same',
  ADDED: 'added',
  REMOVED: 'removed',
  CHANGED: 'changed'
};

function sameCoverage(partner, standard) {
  return partner.events === standard.eventsPerYear && partner.cap === standard.cap;
}

/**
 * One cell of the comparison: the partner coverage, the standard one, and how
 * they differ. `events: null` means unlimited.
 *
 * @param {{events: number|null, cap: number}|undefined} partner
 * @param {{eventsPerYear: number, cap: number}|undefined} standard
 */
export function compareCoverage(partner, standard) {
  if (!partner && !standard) return null;
  if (partner && !standard) return { partner, standard: null, diff: DIFF.ADDED };
  if (!partner) return { partner: null, standard, diff: DIFF.REMOVED };
  return { partner, standard, diff: sameCoverage(partner, standard) ? DIFF.SAME : DIFF.CHANGED };
}

/**
 * Rows of the coverage comparison of a partner: every service any of its
 * plans or their standard counterparts cover, in catalog order, with one
 * compared cell per plan.
 *
 * @param {Array<{name: string, comparesTo: string, coverages: object}>} plans - Partner plans.
 * @param {Array<{code: string, items: Array}>} standardPlans - Plans of the parameters, with caps.
 * @param {Array<string>} serviceOrder - Service codes in catalog order.
 */
export function coverageRows(plans, standardPlans, serviceOrder) {
  const standardOf = (plan) => standardPlans.find((p) => p.code === plan.comparesTo)?.items ?? [];
  const codes = new Set();
  for (const plan of plans) {
    Object.keys(plan.coverages).forEach((code) => codes.add(code));
    standardOf(plan).forEach((item) => codes.add(item.serviceCode));
  }
  const ordered = [
    ...serviceOrder.filter((code) => codes.has(code)),
    ...[...codes].filter((code) => !serviceOrder.includes(code))
  ];
  return ordered.map((code) => ({
    code,
    cells: plans.map((plan) =>
      compareCoverage(plan.coverages[code], standardOf(plan).find((item) => item.serviceCode === code))
    )
  }));
}

/**
 * Pawer's list price for the start of a partner's range: the tier that
 * contains `from` (the first tier when the range starts below it). For a pool
 * it is the tier's own pool price, or the collective one × the flexible
 * factor in parameter versions without pool prices.
 *
 * @param {Array<{from: number, to: number|null, pool?: object|null}>} tiers - Our price list of the country.
 * @param {number} from - First collaborator or pet of the partner's range.
 * @param {string} plan - virtual | wellness | urgencias.
 * @param {number|null} flexibleFactor - Null for a collective price; the pool factor for a pool price.
 * @returns {number|null}
 */
export function listPriceAt(tiers, from, plan, flexibleFactor = null) {
  if (!tiers.length) return null;
  const tier = tiers.find((t) => from >= t.from && (t.to === null || from <= t.to)) ?? tiers[0];
  if (flexibleFactor === null) return tier[plan];
  if (plan !== 'virtual' && tier.pool) return tier.pool[plan];
  return tier[plan] * flexibleFactor;
}

/** How much dearer (+) or cheaper (−) the partner is than our list, as a ratio. */
export function priceGap(partnerPrice, ourPrice) {
  if (!ourPrice) return null;
  return partnerPrice / ourPrice - 1;
}

/**
 * A coverage in one short line: "2 × $20.000" (events a year × cap per
 * event), or "Sin límite · $20.000" when the events are unlimited.
 *
 * @param {number|null} events - Events a year; null for unlimited.
 * @param {string} capText - The cap, already formatted.
 */
export function coverageShort(events, capText) {
  return events === null ? `Sin límite · ${capText}` : `${events} × ${capText}`;
}

function direction(value, standard) {
  if (value === standard) return null;
  if (value === null) return 'up';
  if (standard === null) return 'down';
  return value > standard ? 'up' : 'down';
}

/**
 * Which side of a changed coverage moved against the standard: events and
 * cap, each 'up', 'down' or null when equal. Unlimited events count as more.
 *
 * @param {{events: number|null, cap: number}} partner
 * @param {{eventsPerYear: number|null, cap: number}} standard
 */
export function changeOf(partner, standard) {
  return {
    events: direction(partner.events, standard.eventsPerYear),
    cap: direction(partner.cap, standard.cap)
  };
}
